
































































































import * as _ from 'lodash';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';

import { BaseVue } from '@/BaseVue';
import UiButton from '@/components/ui/UiButton.vue';
import UiSelect2 from '@/components/ui/UiSelect2.vue';
import UiTextEdit from '@/components/ui/UiTextEdit.vue';

@Component({
  components: {
    UiButton,
    UiSelect2,
    UiTextEdit,
  },
})
export default class MultisigInput extends BaseVue {
  public multisigMode = '';
  public minCosigners = '2';
  public xpubs: string[] = ['', ''];

  // Custom mode settings
  public customUseChange = true;
  public customSortPublicKeys = true;
  public customDerivationMode = 'legacy';
  public customPrependPath = '';

  public debouncedValidate = _.debounce(this.validate.bind(this), 300);

  public get multisigModes() {
    return [
      {
        id: 'ElectrumLegacyBTC',
        title: 'Electrum Legacy (xpub)',
        description: 'P2SH - Standard multisig addresses',
      },
      {
        id: 'ElectrumP2SHSegwitBTC',
        title: 'Electrum P2SH Segwit (Ypub)',
        description: 'P2WSH-P2SH - Wrapped SegWit',
      },
      {
        id: 'ElectrumSegwitBTC',
        title: 'Electrum Native Segwit (Zpub)',
        description: 'P2WSH - Native SegWit (bech32)',
      },
      {
        id: 'Bitcoincom',
        title: 'Bitcoin.com',
        description: 'Bitcoin.com wallet format',
      },
      {
        id: 'custom',
        title: 'Custom',
        description: 'Advanced custom configuration',
      },
    ];
  }

  public get derivationModes() {
    return [
      { id: 'legacy', name: 'Legacy' },
      { id: 'p2shSegwit', name: 'P2SH Segwit' },
      { id: 'nativeSegwit', name: 'Native Segwit' },
    ];
  }

  public get minCosignersValid(): boolean {
    const num = Number(this.minCosigners);
    return !isNaN(num) && num > 0 && num <= this.xpubs.length;
  }

  public get minCosignersError(): string {
    const num = Number(this.minCosigners);
    if (isNaN(num)) return 'Must be a valid number';
    if (num <= 0) return 'Must be at least 1';
    if (num > this.xpubs.length) return `Cannot exceed number of keys (${this.xpubs.length})`;
    return '';
  }

  public get validationErrors(): string[] {
    const errors: string[] = [];

    if (!this.multisigMode) {
      errors.push('Multisig mode is required');
    }

    if (!this.minCosignersValid) {
      errors.push(this.minCosignersError);
    }

    const filledXpubs = this.xpubs.filter((x) => x.trim().length > 0);
    if (filledXpubs.length === 0) {
      errors.push('At least one extended public key is required');
    }

    const emptyXpubs = this.xpubs.filter((x) => x.trim().length === 0);
    if (emptyXpubs.length > 0 && filledXpubs.length > 0) {
      errors.push('All extended public key fields must be filled or removed');
    }

    // Check for duplicates
    const uniqueXpubs = new Set(this.xpubs.map((x) => x.trim()).filter((x) => x.length > 0));
    if (uniqueXpubs.size !== filledXpubs.length) {
      errors.push('Extended public keys must be unique');
    }

    if (this.multisigMode === 'custom' && !this.customDerivationMode) {
      errors.push('Derivation mode is required for custom configuration');
    }

    return errors;
  }

  public get isValid(): boolean {
    return this.validationErrors.length === 0;
  }

  public xpubValid(index: number): boolean {
    const xpub = this.xpubs[index].trim();
    if (xpub.length === 0) return false;

    // Check for duplicates
    const otherXpubs = this.xpubs.filter((_, i) => i !== index).map((x) => x.trim());
    if (otherXpubs.includes(xpub)) return false;

    return true;
  }

  public xpubError(index: number): string {
    const xpub = this.xpubs[index].trim();
    if (xpub.length === 0) return 'Extended public key is required';

    const otherXpubs = this.xpubs.filter((_, i) => i !== index).map((x) => x.trim());
    if (otherXpubs.includes(xpub)) return 'This key is already used';

    return '';
  }

  public addXpub() {
    this.xpubs.push('');
  }

  public removeXpub(index: number) {
    if (this.xpubs.length > 1) {
      this.xpubs.splice(index, 1);
      this.emitData();
    }
  }

  public validate() {
    this.$emit('valid', this.isValid);
  }

  public emitData() {
    const data: any = {
      mode: this.multisigMode,
      minCosigners: Number(this.minCosigners),
      hds: this.xpubs.filter((x) => x.trim().length > 0).map((x) => x.trim()),
    };

    // Add custom settings if in custom mode
    if (this.multisigMode === 'custom') {
      data.customUseChange = this.customUseChange;
      data.customSortPublicKeys = this.customSortPublicKeys;
      data.customDerivationMode = this.customDerivationMode;
      if (this.customPrependPath.trim()) {
        data.customPrependPath = this.customPrependPath.trim();
      }
    }

    this.$emit('data', data);
    this.debouncedValidate();
  }

  @Watch('multisigMode')
  public onMultisigModeChanged() {
    this.emitData();
  }

  @Watch('minCosigners')
  public onMinCosignersChanged() {
    this.emitData();
  }

  @Watch('xpubs', { deep: true })
  public onXpubsChanged() {
    this.emitData();
  }

  @Watch('customUseChange')
  @Watch('customSortPublicKeys')
  @Watch('customDerivationMode')
  @Watch('customPrependPath')
  public onCustomSettingsChanged() {
    if (this.multisigMode === 'custom') {
      this.emitData();
    }
  }
}
